import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked, cached } from '@glimmer/tracking';
import ENV from 'tio-employee/config/environment';
import type SessionService from 'tio-employee/services/session';
import type ZendeskService from 'tio-employee/services/zendesk';
import type { Store as StoreService } from '@warp-drive/core';
// Imported as a value (not type-only): `new UserModel()` instantiates the
// extension class as a placeholder before the real record loads.
import { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import type { Company } from 'tio-data/data/resources/company.type';
import type RouterService from 'tio-employee/services/router';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { Agreement as AgreementModel } from 'tio-data/data/resources/agreement.type';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import {
  getSubdomain,
  getPartnerLogoUrl,
  isLocalhost,
  partnerOrigin,
} from 'tio-common/utils/hostname';
import { clearDemoSession } from 'tio-employee/utils/career-pathways-demo-data';
import { findRecord } from '@warp-drive/utilities/json-api';
import { meRequest } from 'tio-data/requests/me';

/** localStorage key holding the company the user last acted within. */
const COMPANY_KEY = 'company';

/** A role the user holds, and the companies they hold it at. */
export interface HeldRole {
  role: string;
  relationshipType: string;
  companyIds: string[];
}

/** Relationship types permitted to edit a TAS application. */
const TAS_EDITOR_RELATIONSHIP_TYPES = [
  'TAS.Admin.PROGRAM_ADMIN',
  'TAS.Admin.SUPERUSER',
  'TAS.Approver.ADMIN_APPROVER',
  'TAS.Approver.APPROVER',
  'TAS.Approver.CUSTOM_APPROVER',
  'TAS.Approver.FINANCE_APPROVER',
  'TAS.Approver.HYPERVISOR',
  'TAS.Approver.PPP_FINANCE_APPROVER',
  'TAS.Approver.TAP_FINANCE_APPROVER',
  'TAS.Approver.TIO_APPROVER',
  'TAS.Approver.SUPERVISOR',
  'TIO_ACCOUNT_MANAGER',
  'TIO_ADMIN',
  'EMPLOYEE_ADMIN',
  'ACCOUNT_OWNER',
];

export const meIncludes = [
  'person.employees.company',
  // Whether the person has linked accounts decides which dashboard tiles render, so it
  // must be known before the first paint; the loans themselves load with the tile.
  'person.accounts',
  'logins',
  'usage-agreement',
  'privacy-agreement',
  'custom-register-agreements.condition',
  'email-addresses',
  // Only for the legacy fallback in `roleClaims`, for an API predating role_claims.
  // Drop once that attribute is everywhere.
  'user-roles.relationships',
  'employees.person',
  'employees.jurisdictive-relationships',
  // shouldShowLpnSurvey reads companyLpnSettings off the EMPLOYEE's company, and it
  // is a sync hasMany — uncached, the company's per-school opt-out reads as absent.
  'employees.company.company-setting.company-lpn-settings.lpn-page',
  'employees.plan',
  // The plan's selected eligibility conditions drive the SLR verification
  // guidelines + the benefit drawer's requirements section; without this
  // include `plan.eligibilityConditions` is always empty.
  'employees.plan.eligibility-conditions',
  'employees.match-participant',
  'employees.tas-participant',
  'employees.tas-eligibilities',
  'employees.employee-addition',
  'employees.pslf-profile',
  // The phone-capture surfaces read verification state off the current employee's
  // MOBILE number; without this every member resolves as having none.
  'employees.phone-numbers',
];

// Omit plan stats and last-disbursed; each runs a query and neither is read from boot.
const planBootFields = [
  'company-key',
  'currency',
  'cadence',
  'milestone-date',
  'waiting-period',
  'waiting-period-unit',
  'description',
  'name',
  'lifetime-limit',
  'annual-limit',
  'limit-excludes-tuition-assistance',
  'check-observations',
  'check-name-on-account',
  'check-parent-plus-loans',
  'check-terms-and-conditions',
  'created-at',
  'enable-pay-parent-plus-loans',
  'is-min-monthly',
  'from-date',
  'day1',
  'staleness-cutoff',
  'custom-attribute-checks',
  'employees',
  'company',
  'tiers',
  'plan-audit-schedule',
  'eligibility-conditions',
];

/**
 * Contains in-memory data and actions relevant to a user's session (as opposed
 * to the persisted data kept in the `session` service).
 *
 * @memberof services
 */
class SessionContextService extends Service {
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare store: StoreService;
  @service declare zendesk: ZendeskService;
  @service declare preRegister: PreRegisterService;

  /**
   * The company the user is currently acting within. Persisted so a multi-company
   * user lands where they left off; `undefined` until they pick or one is inferred.
   */
  @tracked currentCompanyId = localStorage.getItem(COMPANY_KEY) ?? undefined;

  /**
   * The user object of the currently logged-in user, or an empty user object if
   * the user has not yet been loaded into the session context.
   */
  @tracked user!: UserModel;

  /**
   * The company associated with the current session context.
   */
  @tracked company?: Company;

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    this.user = new UserModel();
  }

  /**
   * Whether the authenticated user and relationships have been loaded into the session context.
   */
  get isLoaded() {
    try {
      return !!this.user.id;
    } catch {
      return false;
    }
  }

  /**
   * Whether the user has been authenticated and has a current role.
   */
  get isFullyAuthenticated() {
    return !!(this.session.isAuthenticated && this.currentRole);
  }

  /**
   * Every role the user holds, with the companies it is held at — server-computed,
   * so the client never walks the jurisdiction graph.
   *
   * The wire payload is snake_case (the key-format header rewrites attribute names,
   * not the contents of one), so it is normalized here rather than at each reader.
   */
  @cached
  get roleClaims(): HeldRole[] {
    const claims = this.user?.roleClaims;
    if (claims) {
      return claims.map((claim) => ({
        role: claim.role,
        relationshipType: claim.relationship_type,
        companyIds: claim.company_ids,
      }));
    }

    // Deploy-order safety: an API predating role_claims still describes the roles
    // through the legacy graph. It carries no company ids, so a session on that API
    // holds every role everywhere — which is what it did before any of this.
    return (this.user?.roles ?? []).map((role) => ({
      role: role.role,
      relationshipType: role.relationshipType,
      companyIds: [],
    }));
  }

  /**
   * The employments this session can act through, in the order the API returned
   * them. `employee.company_id` is NOT NULL, which is why company context hangs
   * off these rather than off a role's jurisdiction.
   *
   * Restricted to companies the user actually holds a role at: `person.employees`
   * also carries rows an eligibility file created at a company the person never
   * registered with, which must not become a switchable context.
   */
  @cached
  get availableEmployees(): EmployeeModel[] {
    const employed = (this.person.employees ?? []).filter(
      (employee: EmployeeModel) => employee.isActive && employee.company?.id
    );

    const claimed = new Set(this.roleClaims.flatMap((claim) => claim.companyIds));
    if (!claimed.size) return employed;

    return employed.filter((employee: EmployeeModel) => claimed.has(employee.company.id as string));
  }

  /**
   * The distinct companies the user can act within.
   */
  @cached
  get availableCompanies(): Company[] {
    const byId = new Map<string, Company>();
    for (const employee of this.availableEmployees) {
      const company = employee.company;
      if (company?.id) byId.set(company.id, company);
    }
    return [...byId.values()];
  }

  /**
   * The company the session is acting within.
   *
   * The origin wins: a company is served from its own subdomain, and that subdomain
   * is what the branding and the `x-auth-key` the API scopes by are resolved from.
   * Honouring a stored choice over the host would leave the session presenting one
   * company's key while showing another's data. The stored choice only decides
   * where there is no subdomain to read, i.e. local development.
   */
  @cached
  get currentCompany(): Company | undefined {
    const { availableCompanies, subdomain } = this;

    const served = subdomain
      ? availableCompanies.find((company) => company.companySetting?.subDomain === subdomain)
      : undefined;
    const selected = availableCompanies.find((company) => company.id === this.currentCompanyId);

    return served ?? selected ?? availableCompanies[0] ?? this.company;
  }

  /**
   * The roles the user holds at `currentCompany`.
   *
   * Never narrows to nothing: a session that resolved no company at all still
   * acts under every role it holds, and `currentRole` must stay truthy — the
   * shared TAS actor builder reads it to tell this app from tio-admin, and would
   * otherwise attribute transitions with the admin heuristic.
   */
  @cached
  get rolesForCurrentCompany(): HeldRole[] {
    const companyId = this.currentCompany?.id;
    if (!companyId) return this.roleClaims;

    const scoped = this.roleClaims.filter((claim) => claim.companyIds.includes(companyId));
    return scoped.length ? scoped : this.roleClaims;
  }

  /**
   * A representative role at `currentCompany`, or `undefined` when none is held.
   */
  get currentRole(): HeldRole | undefined {
    return this.rolesForCurrentCompany[0];
  }

  /**
   * The current employee for the user, or an empty employee object if none
   * exists.
   */
  // In an active demo session, `currentEmployee`/`person` derive from the
  // demo `/me` user (seeded by the demo fetch interceptor) exactly like
  // production — no bespoke per-getter demo swap.
  @cached
  get currentEmployee(): EmployeeModel {
    const companyId = this.currentCompany?.id;
    return (
      this.availableEmployees.find(
        (employee: EmployeeModel) => employee.company?.id === companyId
      ) || this.store.createRecord<EmployeeModel>('employee', {})
    );
  }

  @cached
  get person() {
    return this.user.person || this.store.createRecord<PersonModel>('person', {});
  }

  /**
   * Gets the subdomain from the current hostname.
   * Uses shared hostname utility for consistency across services.
   */
  get subdomain() {
    return getSubdomain();
  }

  /**
   * Gets the company logo URL.
   * For non-localhost environments, uses subdomain to construct logo path.
   * For localhost, uses the first employee's company subdomain.
   */
  get logo() {
    if (isLocalhost()) {
      // In localhost, get subdomain from first employee's company setting
      const firstEmployee = this.user.employees?.[0];
      const companySubdomain = firstEmployee?.company?.companySetting?.subDomain;
      return getPartnerLogoUrl(companySubdomain, ENV.tioEnvironment);
    }

    // For non-localhost, use the subdomain from the URL
    return getPartnerLogoUrl(this.subdomain, ENV.tioEnvironment);
  }

  /**
   * Whether the user is viewing the app as an employee.
   */
  get isEmployeeView() {
    return this.holdsRelationshipType('PARTICIPANT') || this.isFamilyMember;
  }

  /** Whether any role held at `currentCompany` matches. */
  holdsRelationshipType = (...types: string[]) =>
    this.rolesForCurrentCompany.some((role) => types.includes(role.relationshipType));

  /** Root-URL entry and company switches both land here; holding both views prefers the dashboard. */
  get landingRoute() {
    return this.isEmployeeView ? 'authenticated.dashboard' : 'authenticated.admin';
  }

  /**
   * Whether the user is viewing the app as a partner admin.
   */
  get isAdminView() {
    return (
      this.isTasRole ||
      this.holdsRelationshipType(
        'ACCOUNT_OWNER',
        'EMPLOYEE_ADMIN',
        'PSLF_ADMIN',
        'TIO_ACCOUNT_MANAGER',
        'TIO_ADMIN'
      )
    );
  }

  get isTasRole() {
    return this.rolesForCurrentCompany.some(
      (role) =>
        role.relationshipType.startsWith('TAS.Approver') ||
        role.relationshipType === 'TAS.Admin.SUPERUSER'
    );
  }

  get canEditTasApplication() {
    return this.holdsRelationshipType(...TAS_EDITOR_RELATIONSHIP_TYPES);
  }

  get isPslfAdmin() {
    return this.holdsRelationshipType('PSLF_ADMIN');
  }

  get isPslfGroupApprover() {
    const relationships = this.currentEmployee.jurisdictiveRelationships;
    return relationships.some((jr) => jr.relationshipType === 'PSLF_GROUP_APPROVER');
  }

  get isFamilyMember() {
    return this.rolesForCurrentCompany.some((role) => role.role === 'FAMILY_MEMBER');
  }

  get isEmailProviderLogin() {
    const userLogin = this.user.logins?.[0];
    return userLogin?.provider === 'EMAIL';
  }

  get isOtherProviderLogin() {
    if (!this.user.id) {
      return false;
    }
    return !this.isEmailProviderLogin;
  }

  get hasAcceptedAppTerms() {
    return !!(
      this.user.usageAgreement &&
      this.user.privacyAgreement &&
      this.hasAcceptedAllCustomRegisterTerms
    );
  }

  get needsToAcceptAppTerms() {
    return this.user && !this.hasAcceptedAppTerms;
  }

  @cached
  get hasAcceptedAllCustomRegisterTerms() {
    const customRegisterAgreementTermsIds = new Set(
      // @ts-expect-error: seems like relation is typed wrong
      (this.user.customRegisterAgreements || []).map((a: AgreementModel) => a.condition?.id)
    );
    const customRegisterTermIds = (this.preRegister.customTerms || []).map((t) => t.id);

    if (customRegisterAgreementTermsIds.size < customRegisterTermIds.length) {
      return false;
    }
    return customRegisterTermIds.every((termId) => {
      return customRegisterAgreementTermsIds.has(termId);
    });
  }

  get isParticipantOnly() {
    const roles = this.rolesForCurrentCompany;
    return roles.length === 1 && roles[0]?.relationshipType === 'PARTICIPANT';
  }

  /**
   * Loads the user data for the current authenticated user.
   */
  load = async () => {
    let content: { data: UserModel };
    try {
      ({ content } = (await this.store.request(
        meRequest(meIncludes, { plans: planBootFields })
      )) as {
        content: { data: UserModel };
      });
    } catch (e) {
      // Any failure here leaves the context without a user, so the session is
      // unusable whatever the cause. Discriminating on `status` stranded callers
      // on failures that carry none — a cache-layer TypeError, say — because the
      // raw rethrow reached routes with no error branch.
      const status = (e as { status?: number })?.status;
      this.logout();
      throw new Error(
        `Unable to load user data for the session${status ? ` (status ${status})` : ''}`,
        { cause: e }
      );
    }
    this.user = content.data;
    if (this.isFamilyMember) {
      // After store.request the included company records are materialized in
      // the cache; for a family-member user the sponsoring company is
      // reachable via the first sideloaded employee's company relationship.
      const chosenCompany = content.data.employees?.[0]?.company;
      if (chosenCompany) {
        this.company = chosenCompany;
      }
    }
  };

  /**
   * Returns the employee IDs fo the current user.
   *
   * HACK: We are struggling with hard-to-reproduce timing bugs that are causing
   *       `sessionContext.user.person.employees` to evaluate as empty
   *       unexpectedly.
   */
  getEmployeeIdsAsync = async () => {
    let employees = this.user?.person?.employees || [];

    if (!employees.length) {
      const { content } = (await this.store.request(
        findRecord<UserModel>('user', this.user.id!, {
          include: 'person.employees',
          reload: true,
        })
      )) as { content: { data: UserModel } };
      employees = content.data.person?.employees || [];
    }
    return employees.map((e) => e.id);
  };

  /**
   * Switches the company the session acts within.
   *
   * Hands off to the company's own origin where it has one. The session does not
   * travel — storage is per-origin — so the user re-authenticates there and the
   * app resolves that company's `x-auth-key` from its subdomain. Swapping in place
   * would serve one company's data under another's key and branding.
   *
   * @param company - One of `availableCompanies`
   */
  setCurrentCompany = (company: Company) => {
    if (!company.id) return;

    const origin = partnerOrigin(company.companySetting?.subDomain);
    if (origin) {
      window.location.assign(origin);
      return;
    }

    // No subdomain to hand off to (local development): stay and switch in place.
    this.currentCompanyId = company.id;
    localStorage.setItem(COMPANY_KEY, company.id);
    this.router.transitionTo(this.landingRoute);
  };

  /**
   * Destroys the authenticated session and logs out the current user.
   */
  logout = async () => {
    this.preRegister.clear();
    try {
      this.zendesk.clearUser();
    } catch (e) {
      console.error('[service:session-context] Could not clear zendesk user', e);
    }

    // This must be last, since this causes a redirect once it is executed
    try {
      await this.session.invalidate();
    } catch (e) {
      console.error('[service:session-context] Could not clear session context', e);
    }

    localStorage.removeItem(COMPANY_KEY);
    localStorage.removeItem('role');
    sessionStorage.clear();

    // Demo mode is sticky in localStorage; clear it on logout so a demo session
    // never survives session invalidation (nor leaks to the next user on a
    // shared device). clearDemoSession also drops the overloaded `role` key.
    try {
      clearDemoSession();
    } catch (e) {
      console.error('[service:session-context] Could not clear demo session', e);
    }

    try {
      this.currentCompanyId = undefined;
      this.user = new UserModel();
    } catch (e) {
      console.error('[service:session-context] Could not clear user and roles', e);
    }
  };
}

export default SessionContextService;
